import { ChangeDetectorRef, Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { BreakpointObserver } from "@angular/cdk/layout";
import { MatButtonModule } from "@angular/material/button";
import { MatChipsModule } from "@angular/material/chips";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { MatDatepickerIntl, MatDatepickerModule } from "@angular/material/datepicker";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatSelectModule } from "@angular/material/select";
import { forkJoin } from "rxjs";

import { Area } from "src/app/shared/model/tickets/area.model";
import { EmpresaLista } from "src/app/shared/model/tickets/empresaLista.model";
import { Estatus } from "src/app/shared/model/tickets/estatus.model";
import { UrlConstantes } from "src/app/utilerias/constantes/urlconstantes";
import { DatepickerIntlEs } from "src/app/utilerias/datepicker-intl-es";
import { FECHA_ESTANDAR_FORMATS, FechaEstandarAdapter } from "src/app/utilerias/fecha-estandar.adapter";
import { TicketsService } from "../../tickets.service";
import { ModalReporteComponent } from "./modal-reporte/modal-reporte.component";
import {
  FiltrosReporteTickets,
  copiarFiltros,
  descargarReporteTickets,
  motivoDelError,
  resumenFiltros,
  sugerenciaSinTickets,
} from "./reporte-tickets.util";

/** Lo que manda el tablero al abrir el diálogo: el área que tiene seleccionada (0 = todas). */
export interface DatosDialogoReporteTickets {
  idArea?: number | null;
}

type PeriodoRapido = "hoy" | "7dias" | "30dias" | "mes" | "mesAnterior" | "anio";

const PERIODO_POR_OMISION: PeriodoRapido = "30dias";

/** dd/mm/aaaa con día, mes y año de 4 cifras; lo demás no es una fecha para este campo. */
const FECHA_ESCRITA = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

/**
 * FechaEstandarAdapter, estricto. El estándar manda cualquier texto que no
 * sea dd/mm/aaaa a Date.parse, que lee "1/9/26" como 9 de enero (M/D/AA) y
 * "2026-10-05" en UTC (el 4 en México), y convierte un día imposible
 * (31/02) en null, que el control toma como vacío. Aquí todo texto que no
 * sea un dd/mm/aaaa real es una fecha inválida: el campo marca
 * matDatepickerParse y el periodo dice cómo escribirla.
 */
class FechaReporteAdapter extends FechaEstandarAdapter {
  override parse(value: unknown, parseFormat?: unknown): Date | null {
    if (typeof value === "string" && value.trim()) {
      return FECHA_ESCRITA.test(value.trim()) ? super.parse(value, parseFormat) ?? this.invalid() : this.invalid();
    }
    return super.parse(value, parseFormat);
  }
}

/**
 * Filtros del reporte de tickets (botón REPORTE del tablero): descarga el
 * Excel o abre la vista previa con los mismos filtros.
 *
 * Las dos fechas son obligatorias y la inicial no puede pasar a la final;
 * así el servidor nunca recibe un periodo a medias. Todo lo que pasa (sin
 * tickets, error, descarga lista) se avisa dentro del propio diálogo.
 *
 * La app corre sin zone.js: tras cada respuesta HTTP la vista se repinta a
 * mano (pintar()), en el next y en el error.
 */
@Component({
  selector: "app-reportes-tickets",
  standalone: true,
  templateUrl: "./reportes-tickets.component.html",
  styleUrls: ["./reporte-tickets.compartido.css", "./reportes-tickets.component.css"],
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
  ],
  providers: [
    // Calendario en español y con el dd/MM/yyyy de COS, como en Permisos.
    { provide: MAT_DATE_LOCALE, useValue: "es-MX" },
    { provide: DateAdapter, useClass: FechaReporteAdapter },
    { provide: MAT_DATE_FORMATS, useValue: FECHA_ESTANDAR_FORMATS },
    { provide: MatDatepickerIntl, useClass: DatepickerIntlEs },
  ],
})
export class ReportesTicketsComponent implements OnInit {
  private readonly ticketsService = inject(TicketsService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  // Opcionales: la ruta TICKETS/SolicitudReportes monta el componente sin diálogo.
  private readonly dialogRef = inject<MatDialogRef<ReportesTicketsComponent>>(MatDialogRef, { optional: true });
  private readonly datos = inject<DatosDialogoReporteTickets | null>(MAT_DIALOG_DATA, { optional: true });

  readonly periodos: ReadonlyArray<{ id: PeriodoRapido; etiqueta: string }> = [
    { id: "hoy", etiqueta: "Hoy" },
    { id: "7dias", etiqueta: "Últimos 7 días" },
    { id: "30dias", etiqueta: "Últimos 30 días" },
    { id: "mes", etiqueta: "Este mes" },
    { id: "mesAnterior", etiqueta: "Mes anterior" },
    { id: "anio", etiqueta: "Este año" },
  ];

  /** Tope del calendario: no hay tickets del futuro. */
  readonly hoy = soloDia(new Date());
  /** Piso del calendario: ningún ticket es anterior; evita años mal escritos (0202). */
  readonly minimo = new Date(2000, 0, 1);
  readonly touchUi = inject(BreakpointObserver).isMatched("(max-width: 575.98px)");

  areas: Area[] = [];
  estatus: Estatus[] = [];
  empresas: EmpresaLista[] = [];
  cargandoCatalogos = false;
  errorCatalogos = false;
  generando = false;

  /** Aviso del último intento de descarga; cualquier cambio de filtro lo quita. */
  resultado: "vacio" | "error" | "exito" | null = null;
  resumenVacio = "";
  sugerenciaVacio = "";
  detalleError = "";

  @ViewChild("botonDescargar") private botonDescargar?: ElementRef<HTMLButtonElement>;

  readonly form = new FormGroup({
    area: new FormControl(this.idAreaDelTablero(), { nonNullable: true }),
    estatus: new FormControl(0, { nonNullable: true }),
    empresa: new FormControl(0, { nonNullable: true }),
    rango: new FormGroup({
      inicio: new FormControl<Date | null>(null, Validators.required),
      fin: new FormControl<Date | null>(null, Validators.required),
    }),
  });

  /** Chip de periodo rápido; null cuando las fechas se escribieron a mano. */
  readonly periodo = new FormControl<PeriodoRapido | null>(PERIODO_POR_OMISION);

  private aplicandoPeriodo = false;
  private destruido = false;

  constructor() {
    // Antes que cualquier takeUntilDestroyed: al destruir, el guardia ya
    // está puesto cuando los flujos se cierran.
    this.destroyRef.onDestroy(() => (this.destruido = true));
    this.ponerFechas(rangoDe(PERIODO_POR_OMISION));
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.resultado = null;
    });

    // Escribir o elegir fechas en el calendario suelta el chip.
    this.form.controls.rango.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.aplicandoPeriodo && this.periodo.value !== null) {
        this.periodo.setValue(null, { emitEvent: false });
      }
    });

    this.periodo.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((periodo) => {
      // Soltar el chip seleccionado deja las fechas como están.
      if (periodo) {
        this.ponerFechas(rangoDe(periodo));
        this.resultado = null;
      }
    });

    this.cargarCatalogos();
  }

  get hayVariasEmpresas(): boolean {
    return this.empresas.length > 1;
  }

  get catalogosListos(): boolean {
    return !this.cargandoCatalogos && !this.errorCatalogos;
  }

  get puedeGenerar(): boolean {
    return this.form.valid && this.catalogosListos && !this.generando;
  }

  /**
   * Un solo mensaje para el periodo, el más útil primero. Lo mal escrito
   * (31/02, 1/9/26, 2026-10-05) llega como matDatepickerParse gracias a
   * FechaReporteAdapter.
   */
  errorPeriodo(): string {
    const { inicio, fin } = this.form.controls.rango.controls;
    if (inicio.hasError("matDatepickerParse") || fin.hasError("matDatepickerParse")) {
      return "Escribe la fecha como dd/mm/aaaa.";
    }
    if (inicio.hasError("required")) {
      return "Indica desde qué fecha.";
    }
    if (fin.hasError("required")) {
      return "Indica hasta qué fecha.";
    }
    if (inicio.hasError("matDatepickerMax") || fin.hasError("matDatepickerMax")) {
      return "La fecha no puede ser posterior a hoy.";
    }
    if (inicio.hasError("matDatepickerMin") || fin.hasError("matDatepickerMin")) {
      return "La fecha no puede ser anterior al 01/01/2000.";
    }
    if (inicio.hasError("matStartDateInvalid") || fin.hasError("matEndDateInvalid")) {
      return "La fecha final no puede ser anterior a la inicial.";
    }
    return "";
  }

  cargarCatalogos(): void {
    // Se llega desde ngOnInit o desde el clic en Reintentar: Angular repinta
    // al salir de ahí; el repintado manual va en la respuesta.
    this.cargandoCatalogos = true;
    this.errorCatalogos = false;
    this.bloquearCatalogos(true);

    forkJoin({
      areas: this.ticketsService.consultarArea(UrlConstantes.consultarArea),
      estatus: this.ticketsService.consultarEstatus(),
      empresas: this.ticketsService.consultarEmpresa(UrlConstantes.consultarEmpresaLista),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ areas, estatus, empresas }) => {
          this.areas = Array.isArray(areas?.list) ? areas.list : [];
          this.estatus = Array.isArray(estatus?.list) ? estatus.list : [];
          this.empresas = Array.isArray(empresas?.list) ? empresas.list : [];
          this.cargandoCatalogos = false;
          this.bloquearCatalogos(false);
          // El área del tablero solo si existe en el catálogo; si no, todas.
          const { area, empresa } = this.form.controls;
          if (!this.areas.some((item) => item.id === area.value)) {
            area.setValue(0, { emitEvent: false });
          }
          // Con una sola empresa el campo no se muestra: el tenant ya acota
          // el reporte, y filtrar por ella dejaba fuera los tickets dirigidos.
          if (!this.hayVariasEmpresas || !this.empresas.some((item) => item.id === empresa.value)) {
            empresa.setValue(0, { emitEvent: false });
          }
          this.pintar();
        },
        error: () => {
          this.cargandoCatalogos = false;
          this.errorCatalogos = true;
          this.pintar();
        },
      });
  }

  /** Vuelve a los valores con que abre el diálogo. */
  limpiar(): void {
    const { inicio, fin } = rangoDe(PERIODO_POR_OMISION);
    const idArea = this.idAreaDelTablero();
    this.form.reset(
      {
        area: this.areas.some((item) => item.id === idArea) ? idArea : 0,
        estatus: 0,
        empresa: 0,
        rango: { inicio, fin },
      },
      { emitEvent: false }
    );
    this.periodo.setValue(PERIODO_POR_OMISION, { emitEvent: false });
    this.resultado = null;
  }

  cerrar(): void {
    if (!this.generando) {
      this.dialogRef?.close();
    }
  }

  descargar(): void {
    if (!this.puedeGenerar) {
      this.form.markAllAsTouched();
      return;
    }
    const filtros = this.filtros();
    this.resultado = null;
    this.ponerGenerando(true);

    descargarReporteTickets(this.ticketsService, filtros)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (respuesta) => {
          this.ponerGenerando(false);
          if (respuesta.tipo === "archivo") {
            this.resultado = "exito";
          } else {
            this.resultado = "vacio";
            this.resumenVacio = resumenFiltros(filtros).join(" · ");
            this.sugerenciaVacio = sugerenciaSinTickets(filtros);
          }
          this.pintar();
          this.devolverFoco();
        },
        error: (error) => {
          this.ponerGenerando(false);
          this.resultado = "error";
          this.detalleError = motivoDelError(error, "Revisa tu conexión e intenta de nuevo.");
          this.pintar();
          this.devolverFoco();
        },
      });
  }

  verVistaPrevia(): void {
    if (!this.puedeGenerar) {
      this.form.markAllAsTouched();
      return;
    }
    this.resultado = null;
    // Debajo del topbar (fixed, z-index 12000), igual que el diálogo de filtros.
    this.dialog.open<ModalReporteComponent, FiltrosReporteTickets>(ModalReporteComponent, {
      data: copiarFiltros(this.filtros()),
      width: "min(1200px, calc(100vw - 32px))",
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(100vh - var(--solser-topbar-height, 68px) - 32px)",
      position: { top: "calc(var(--solser-topbar-height, 68px) + 16px)" },
      panelClass: "app-standard-dialog",
      backdropClass: "app-standard-dialog-backdrop",
      autoFocus: "dialog",
      restoreFocus: true,
      ariaLabelledBy: "tickets-reporte-preview-titulo",
    });
  }

  // ------------------------------------------------------------------

  private filtros(): FiltrosReporteTickets {
    const { area, estatus, empresa, rango } = this.form.getRawValue();
    const idEmpresa = this.hayVariasEmpresas ? empresa : 0;
    return {
      inicio: rango.inicio,
      fin: rango.fin,
      idArea: area,
      idEstatus: estatus,
      idEmpresa,
      nombreArea: texto(this.areas.find((item) => item.id === area)?.area),
      nombreEstatus: texto(this.estatus.find((item) => item.id === estatus)?.nombre),
      nombreEmpresa: idEmpresa ? texto(this.empresas.find((item) => item.id === idEmpresa)?.razon_social) : "",
      conEmpresa: this.hayVariasEmpresas,
    };
  }

  private idAreaDelTablero(): number {
    const id = Number(this.datos?.idArea);
    return Number.isInteger(id) && id > 0 ? id : 0;
  }

  private ponerFechas(rango: { inicio: Date; fin: Date }): void {
    this.aplicandoPeriodo = true;
    this.form.controls.rango.setValue(rango);
    this.aplicandoPeriodo = false;
  }

  private bloquearCatalogos(bloquear: boolean): void {
    const { area, estatus, empresa } = this.form.controls;
    for (const control of [area, estatus, empresa]) {
      if (bloquear) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
  }

  /** Mientras se genera, nada se mueve: ni filtros, ni Escape, ni clic fuera. */
  private ponerGenerando(generando: boolean): void {
    this.generando = generando;
    if (this.dialogRef) {
      this.dialogRef.disableClose = generando;
    }
    if (generando) {
      this.form.disable({ emitEvent: false });
      this.periodo.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
      this.periodo.enable({ emitEvent: false });
    }
  }

  private pintar(): void {
    if (this.destruido) {
      return;
    }
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  /**
   * Al deshabilitarse mientras se genera, el botón enfocado suelta el foco
   * y el navegador lo manda a <body>: quien usa teclado o lector de
   * pantalla quedaba fuera del diálogo. Ya repintado (el botón otra vez
   * habilitado), el foco regresa a Descargar si nadie más lo tomó.
   */
  private devolverFoco(): void {
    const boton = this.botonDescargar?.nativeElement;
    const activo = document.activeElement;
    if (!this.destruido && boton && !boton.disabled && (!activo || activo === document.body)) {
      boton.focus();
    }
  }
}

function soloDia(fecha: Date): Date {
  return new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
}

function rangoDe(periodo: PeriodoRapido): { inicio: Date; fin: Date } {
  const hoy = soloDia(new Date());
  const anio = hoy.getFullYear();
  const mes = hoy.getMonth();
  const haceDias = (dias: number) => new Date(anio, mes, hoy.getDate() - dias);
  switch (periodo) {
    case "hoy":
      return { inicio: hoy, fin: new Date(hoy) };
    case "7dias":
      return { inicio: haceDias(6), fin: hoy };
    case "mes":
      return { inicio: new Date(anio, mes, 1), fin: hoy };
    case "mesAnterior":
      return { inicio: new Date(anio, mes - 1, 1), fin: new Date(anio, mes, 0) };
    case "anio":
      return { inicio: new Date(anio, 0, 1), fin: hoy };
    case "30dias":
    default:
      return { inicio: haceDias(29), fin: hoy };
  }
}

function texto(valor: Area["area"] | Estatus["nombre"] | EmpresaLista["razon_social"]): string {
  return valor === null || valor === undefined ? "" : String(valor).trim();
}
